import React from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { useLazyLookupTideQuery } from "../../store/api";

export default function TideLookup() {
  const [lat, setLat] = React.useState("");
  const [lon, setLon] = React.useState("");
  const [dateTime, setDateTime] = React.useState("");
  const [result, setResult] = React.useState(undefined);
  const [error, setError] = React.useState(undefined);

  const [triggerLookup, { isFetching }] = useLazyLookupTideQuery();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setResult(undefined);
    setError(undefined);

    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);
    if (Number.isNaN(latNum) || Number.isNaN(lonNum) || !dateTime) {
      setError("Enter a valid latitude, longitude, and date/time.");
      return;
    }

    try {
      const response = await triggerLookup({
        lat: latNum,
        lon: lonNum,
        time: dateTime,
      }).unwrap();
      setResult(response);
    } catch (_err) {
      setError(
        "Could not look up the tide. Check the coordinates and date/time, or try again."
      );
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 5, maxWidth: 900 }} variant="outlined">
      <Typography variant="h6" gutterBottom>
        Tide lookup
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Look up the predicted tide height (feet above MLLW) at a location and
        time using the nearest NOAA tide station. Date/time is interpreted as
        UTC/GMT to match NOAA CSB data.
      </Typography>
      <form onSubmit={handleSubmit}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          sx={{ mt: 2 }}
        >
          <TextField
            label="Latitude"
            value={lat}
            onChange={(event) => setLat(event.target.value)}
            size="small"
            placeholder="31.0468"
          />
          <TextField
            label="Longitude"
            value={lon}
            onChange={(event) => setLon(event.target.value)}
            size="small"
            placeholder="-81.4226"
          />
          <TextField
            label="Date/time (UTC)"
            type="datetime-local"
            value={dateTime}
            onChange={(event) => setDateTime(event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <Button
            type="submit"
            variant="outlined"
            size="medium"
            disabled={isFetching}
          >
            {isFetching ? "Looking up..." : "Look up tide"}
          </Button>
        </Stack>
      </form>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Stack spacing={1} sx={{ mt: 3 }}>
          <Typography>
            {result.tideFt != null ? (
              <>
                Tide: <strong>{result.tideFt.toFixed(2)} ft</strong> above MLLW
              </>
            ) : (
              "No tide prediction available at that time."
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Station: {result.stationName} ({result.stationId}),{" "}
            {result.distanceNm.toFixed(1)} nm away
          </Typography>
        </Stack>
      )}
    </Paper>
  );
}
